import dayjs from 'dayjs'
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch'
import { useForm, UseFormReturn } from 'react-hook-form'
import React, { useContext, useState } from 'react'
import { AppModalContext } from '../../../../../../components/AppModal/AppContextModal'
import { useAlert } from 'react-alert'
import useIsMounted from '../../../../../../hooks/useIsMounted'
import { NewUserForm, NewUserFormEmployments, NewUserFormFields, NewUserFormLocalization, NewUserFormPersonal } from './types'
import { User, UserEmployment, UserExtra, UserLocalization, UserPersonal } from '../../../../../../types/user'
import {
	createUser,
	updateAnyUser,
} from '../../../../../../store/reducers/user/actions'
import { useTypedSelector } from '../../../../../../hooks/useTypedSelector'
import { selectSelectedCoach } from '../../../../../../store/reducers/coaches/selectors'
import { selectUserId } from '../../../../../../store/reducers/user/selectors'
import { selectNewUserAva, selectNewUserCV } from '../../../../../../store/reducers/clinicManagement/newUser/selectors'

const useNewUserForm = (
	formDefaultValue?: NewUserForm | undefined,
	editForm?: NewUserFormEmployments | NewUserFormLocalization | NewUserFormPersonal,
	update?: boolean
): [
		boolean,
		boolean,
		UseFormReturn<NewUserForm>,
		(data: NewUserForm) => void,
		React.MouseEventHandler<HTMLButtonElement>
	] => {
	const dispatch = useAppDispatch()
	const alert = useAlert()
	const isMounted = useIsMounted()
	const { onCloseModal } = useContext(AppModalContext)
	const [loading, setLoading] = useState<boolean>(false)
	const coach = useTypedSelector(selectSelectedCoach)
	const userId = useTypedSelector(selectUserId)
	const cv = useTypedSelector(selectNewUserCV)
	const ava = useTypedSelector(selectNewUserAva)
	const isUser = userId === coach._id

	const formMethods = useForm<NewUserForm>({
		mode: 'all',
		defaultValues: formDefaultValue || editForm,
	})

	const onSubmit = async (data: NewUserForm): Promise<void> => {
		const reqDataEmployment: UserEmployment = {
			accounts: data[NewUserFormFields.NEW_USER_ACCOUNTS] || coach?.employment?.accounts,
			employer: data[NewUserFormFields.NEW_USER_ORGANIZATION] || coach?.employment?.employer,
			expertise: data[NewUserFormFields.NEW_USER_EXPERTISE] || coach?.employment?.expertise,
			patientCapacityLoad: coach?.employment?.patientCapacityLoad,
			roles: data[NewUserFormFields.NEW_USER_ROLE] || coach.employment.roles,
			skills: data[NewUserFormFields.NEW_USER_SKILLS] || coach.employment.skills,
			startOfService: dayjs(
				data[NewUserFormFields.NEW_USER_DATE_STARTED]
			).format('M/D/YY h:mm A') || coach.employment.startOfService,
			title: data[NewUserFormFields.NEW_USER_PREFIX] || coach.employment.title,
			roleTypes: data[NewUserFormFields.NEW_USER_TIRE] || coach.employment.roleTypes,
		}
		const reqDataExtra: UserExtra = {
			organizationID: data[NewUserFormFields.NEW_USER_ORGANIZATION] || coach.extra.organizationID
		}
		const reqDataLocalization: UserLocalization = {
			carbs: data[NewUserFormFields.NEW_USER_CARBS] || coach?.localization?.carbs,
			dateConfiguration: data[NewUserFormFields.NEW_USER_DATE_CONFIG]
				|| coach?.localization?.dateConfiguration,
			glucoseLevel: data[NewUserFormFields.NEW_USER_GLUCOSE] || coach?.localization?.glucoseLevel,
			language: data[NewUserFormFields.NEW_USER_LANG] || coach?.localization?.language,
			temperature: data[NewUserFormFields.NEW_USER_TEMP] || coach?.localization?.temperature,
			weightHeight: data[NewUserFormFields.NEW_USER_UNITS] || coach?.localization?.weightHeight,
		}
		const reqDataPersonal: UserPersonal = {
			name:
				(data[NewUserFormFields.NEW_USER_FIRST_NAME] || coach.personal.firstName) +
				' ' +
				(data[NewUserFormFields.NEW_USER_SECOND_NAME] || coach.personal.lastName),
			firstName: data[NewUserFormFields.NEW_USER_FIRST_NAME] || coach.personal.firstName,
			lastName: data[NewUserFormFields.NEW_USER_SECOND_NAME] || coach.personal.lastName,
			email: data[NewUserFormFields.NEW_USER_EMAIL] || coach.personal.email,
			imageUrl: ava || coach?.personal?.imageUrl,
			cvUrl: cv || coach?.personal?.cvUrl,
			dateOfBirth: dayjs(
				data[NewUserFormFields.NEW_USER_DATE_BIRTH]
			).format('M/D/YY h:mm A') || coach.personal.dateOfBirth,
			gender: data[NewUserFormFields.NEW_USER_GENDER_BIRTH] || coach.personal.gender,
			phone: data[NewUserFormFields.NEW_USER_PHONE] || coach.personal.phone,
			address1: data[NewUserFormFields.NEW_USER_ADDRESS_1] || coach.personal.address1,
			address2: data[NewUserFormFields.NEW_USER_ADDRESS_2] || coach.personal.address2,
			city: data[NewUserFormFields.NEW_USER_CITY] || coach.personal.city,
			ZipCode: data[NewUserFormFields.NEW_USER_ZIP] || coach.personal.ZipCode,
			Country: data[NewUserFormFields.NEW_USER_COUNTRY] || coach.personal.Country,
			state: data[NewUserFormFields.NEW_USER_STATE] || coach.personal.state,
			notes: data[NewUserFormFields.NEW_USER_NOTES] || coach.personal.notes,
		}
		const reqDataNew: User = {
			personal: reqDataPersonal,
			notes: data[NewUserFormFields.NEW_USER_BIO] || coach?.notes,
			localization: reqDataLocalization,
			employment: reqDataEmployment,
			extra: reqDataExtra
		}
		const newBoby = Object.assign({}, coach, reqDataNew)
		try {
			setLoading(true)
			if ((editForm || update) && isUser) {
				await dispatch(updateAnyUser(newBoby, userId, false))
			} else if ((editForm || update) && !isUser) {
				await dispatch(updateAnyUser(newBoby, coach?._id, true))
			} else { await dispatch(createUser(reqDataNew)) }
		} catch (e: any) {
			'message' in e && alert.error(e.message)
		} finally {
			if (isMounted()) {
				setLoading(false)
				onCloseModal && onCloseModal()
			}
		}
	}

	const handleCancel: React.MouseEventHandler<HTMLButtonElement> = () => {
		onCloseModal()
	}

	const saveButtonDisabled = loading || !formMethods.formState.isValid

	return [loading, saveButtonDisabled, formMethods, onSubmit, handleCancel]
}

export default useNewUserForm
